import * as React from 'react';
import { cn } from '@/lib/utils/cn';
import { ChartTooltip, useChartTooltip, useSeriesKeys } from './chart-tooltip';
import { ChartEmpty, ChartLegend, SrTable, f1, formatMoney } from './shared';
import { useEntryMotion } from './use-entry-motion';

/**
 * SpendCurve — cumulative spend against plan (prototype `sCurve()`): the
 * plan as a light line, the actual to date as an ink line with a faint
 * wash under it and a dot plus "$X spent" at the last actual, the
 * projection as a dashed line on from there, and the authorised ceiling
 * (contract + approved change orders) as a red rule labelled
 * "Authorised $X". Hover a month, or focus the chart and use the arrow
 * keys, to read it.
 *
 * Every number is a prop: the plan months, the actuals, the projection
 * and the ceiling come from the budget API (E22 design §2). The chart
 * only picks a round axis top that fits them.
 */
export interface SpendMonth {
  /** Axis label ("Apr"). */
  label: string;
  /** Cumulative planned spend to the end of the month, dollars. */
  plan: number;
  /** Cumulative actual spend; null for months not yet reported. */
  actual: number | null;
  /** Cumulative projection; null where there is none (before today). */
  projected: number | null;
}

export interface SpendCurveProps {
  months: SpendMonth[];
  /** The authorised ceiling (revised contract), dollars. */
  authorised: number | null;
  width?: number;
  height?: number;
  motionKey?: string | number;
  className?: string;
}

const M = { l: 48, r: 112, t: 12, b: 26 };

/** A round axis: steps of 1, 2, 2.5 or 5 × 10ⁿ, about five of them. */
function niceAxis(max: number): { top: number; step: number } {
  if (!(max > 0)) return { top: 1_000_000, step: 250_000 };
  const rough = max / 5;
  const pow = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 2.5, 5, 10].map((k) => k * pow).find((s) => s >= rough) ?? 10 * pow;
  return { top: Math.ceil(max / step) * step, step };
}

function axisMoney(v: number): string {
  if (v === 0) return '$0';
  if (v >= 1e6) return `$${Number((v / 1e6).toFixed(2))}M`;
  return `$${Math.round(v / 1000)}k`;
}

export function SpendCurve({
  months,
  authorised,
  width: w = 720,
  height: h = 220,
  motionKey,
  className,
}: SpendCurveProps) {
  const { animate, runKey } = useEntryMotion(motionKey);
  const { tip, show, hide } = useChartTooltip();
  const svgRef = React.useRef<SVGSVGElement>(null);
  const n = months.length;
  const pw = w - M.l - M.r;
  const ph = h - M.t - M.b;
  const values = months.flatMap((m) => [m.plan, m.actual ?? 0, m.projected ?? 0]);
  const { top, step } = niceAxis(Math.max(authorised ?? 0, ...values));
  const x = (i: number) => (n <= 1 ? M.l + pw / 2 : M.l + (i / (n - 1)) * pw);
  const y = (v: number) => M.t + ph - (v / top) * ph;

  const reading = (i: number) => {
    const m = months[i];
    if (!m) return null;
    const v = m.actual ?? m.projected ?? m.plan;
    return {
      at: { x: x(i), y: y(v) },
      content: {
        title: m.label,
        value:
          m.actual !== null
            ? `${formatMoney(m.actual)} spent`
            : m.projected !== null
              ? `${formatMoney(m.projected)} projected`
              : `${formatMoney(m.plan)} planned`,
        sub: `plan ${formatMoney(m.plan)}`,
      },
    };
  };
  const showMonth = (i: number) => {
    const svg = svgRef.current;
    const r = reading(i);
    if (!svg || !r) return;
    const box = svg.getBoundingClientRect();
    const scale = box.width / w || 1;
    show(r.content, { x: box.left + r.at.x * scale, y: box.top + r.at.y * scale });
  };
  const keys = useSeriesKeys(n, (i) => (i === null ? hide() : showMonth(i)));

  if (!n) {
    return (
      <ChartEmpty title="No spend plan yet" className={className}>
        The contract value comes from the SOW; add the monthly plan and the actuals to draw the
        curve.
      </ChartEmpty>
    );
  }

  const path = (pts: Array<[number, number]>) =>
    pts.map(([i, v], k) => `${k ? 'L' : 'M'}${f1(x(i))} ${f1(y(v))}`).join(' ');
  const plan = months.map((m, i): [number, number] => [i, m.plan]);
  const actual = months.flatMap(
    (m, i): Array<[number, number]> => (m.actual === null ? [] : [[i, m.actual]]),
  );
  const lastActual = actual[actual.length - 1];
  let projection = months.flatMap(
    (m, i): Array<[number, number]> =>
      m.projected === null || (lastActual && i < lastActual[0]) ? [] : [[i, m.projected]],
  );
  // The dashed line leaves from the last actual point.
  const firstProj = projection[0];
  if (lastActual && firstProj && firstProj[0] > lastActual[0])
    projection = [lastActual, ...projection];

  const ticks: number[] = [];
  for (let v = 0; v <= top + step / 2; v += step) ticks.push(v);
  const colW = n > 1 ? pw / (n - 1) : pw;

  return (
    <div key={runKey} className={cn(animate && 'chart-anim', className)}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-label="Cumulative spend against plan"
        tabIndex={0}
        className="chart-svg block h-auto w-full overflow-visible rounded-[2px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-amber-600"
        {...keys.handlers}
      >
        {ticks.map((v) => (
          <g key={`t${v}`}>
            <line x1={M.l} x2={M.l + pw} y1={f1(y(v))} y2={f1(y(v))} stroke="var(--ink-100)" />
            <text x={M.l - 8} y={f1(y(v) + 4)} textAnchor="end">
              {axisMoney(v)}
            </text>
          </g>
        ))}
        {authorised !== null ? (
          <>
            <line
              x1={M.l}
              x2={M.l + pw}
              y1={f1(y(authorised))}
              y2={f1(y(authorised))}
              stroke="var(--posture-critical)"
              strokeWidth={1}
            />
            <text x={M.l + pw + 6} y={f1(y(authorised) + 4)}>
              Authorised {formatMoney(authorised)}
            </text>
          </>
        ) : null}
        <path
          className="a-draw"
          pathLength={1}
          d={path(plan)}
          fill="none"
          stroke="var(--ink-300)"
          strokeWidth={2}
        />
        {lastActual ? (
          <>
            <path
              d={`${path(actual)} L${f1(x(lastActual[0]))} ${f1(y(0))} L${f1(x(actual[0]?.[0] ?? 0))} ${f1(y(0))} Z`}
              fill="var(--ink-900)"
              opacity={0.06}
            />
            <path
              className="a-draw"
              pathLength={1}
              d={path(actual)}
              fill="none"
              stroke="var(--ink-900)"
              strokeWidth={2}
              strokeLinecap="round"
            />
          </>
        ) : null}
        {projection.length > 1 ? (
          <path
            d={path(projection)}
            fill="none"
            stroke="var(--ink-700)"
            strokeWidth={2}
            strokeDasharray="5 4"
          />
        ) : null}
        {lastActual ? (
          <>
            <circle
              className="a-fade"
              cx={f1(x(lastActual[0]))}
              cy={f1(y(lastActual[1]))}
              r={5}
              fill="var(--ink-900)"
              stroke="var(--paper-0)"
              strokeWidth={2}
            />
            <text
              className="a-fade fill-ink-900 font-semibold"
              x={f1(x(lastActual[0]) - 8)}
              y={f1(y(lastActual[1]) - 10)}
              textAnchor="end"
            >
              {formatMoney(lastActual[1])} spent
            </text>
          </>
        ) : null}
        {months.map((m, i) => (
          <text key={`m${i}`} x={f1(x(i))} y={h - 6} textAnchor="middle">
            {m.label}
          </text>
        ))}
        {months.map((m, i) => (
          <rect
            key={`h${i}`}
            x={f1(x(i) - colW / 2)}
            y={M.t}
            width={f1(colW)}
            height={ph}
            fill="transparent"
            onPointerEnter={() => keys.setIndex(i)}
            onPointerLeave={() => keys.setIndex(null)}
          />
        ))}
      </svg>
      <SrTable
        caption={`Cumulative spend against plan${authorised !== null ? `; authorised ${formatMoney(authorised)}` : ''}`}
        head={['Month', 'Plan', 'Actual', 'Projected']}
        rows={months.map((m) => [
          m.label,
          formatMoney(m.plan),
          m.actual === null ? '' : formatMoney(m.actual),
          m.projected === null ? '' : formatMoney(m.projected),
        ])}
      />
      <ChartTooltip tip={tip} />
    </div>
  );
}

/** The budget card's key. */
export function SpendLegend({ className }: { className?: string }) {
  return (
    <ChartLegend
      className={cn('mt-1.5', className)}
      items={[
        { label: 'Plan', swatch: 'line', color: 'bg-ink-300' },
        { label: 'Actual', swatch: 'line', color: 'bg-ink-900' },
        { label: 'Projected', swatch: 'dash', color: 'border-ink-700' },
        { label: 'Authorised ceiling', swatch: 'line', color: 'bg-posture-critical' },
      ]}
    />
  );
}
